import * as fs from 'fs';
import * as path from 'path';
import { parse } from '@babel/parser';
import { default as traverse } from '@babel/traverse'; // 修改为命名导入
import type { CommentBlock, CommentLine } from '@babel/types';

export interface FunctionInfo {
  name: string;
  description?: string;
  summary?: string;
}
/**
 * 从 JavaScript 代码字符串中提取导出的函数名及其注释
 * @param code - JavaScript 代码字符串
 * @returns 包含函数名和注释的对象数组
 */
export const extractFunctionsFromCode = (code: string): Array<FunctionInfo> => {
  try {
    // 解析为 AST（支持 ES6+ 语法）
    const ast = parse(code, {
      sourceType: 'module',
      plugins: ['jsx', 'typescript'], // 支持 JSX 和 TypeScript
      attachComment: true, // 启用注释解析
    });

    const exportedFunctions: Array<FunctionInfo> = [];

    // 遍历 AST
    traverse(ast, {
      ExportNamedDeclaration(path) {
        // 提取函数上方的注释
        const comments = path.node.leadingComments || [];
        const description = extractCommentValue(comments, 'description');
        const summary = extractCommentValue(comments, 'summary');

        // 处理导出的函数声明
        if (path.node.declaration?.type === 'FunctionDeclaration') {
          const functionName = path.node.declaration.id?.name;
          if (functionName) {
            exportedFunctions.push({ name: functionName, description, summary });
          }
        }

        // 处理导出的变量声明（函数表达式或箭头函数）
        if (path.node.declaration?.type === 'VariableDeclaration') {
          path.node.declaration.declarations.forEach((declaration) => {
            if (
              declaration.init?.type === 'FunctionExpression' ||
              declaration.init?.type === 'ArrowFunctionExpression'
            ) {
              const functionName = (declaration.id as any)?.name;
              if (functionName) {
                exportedFunctions.push({ name: functionName, description, summary });
              }
            }
          });
        }
      },
    });

    return exportedFunctions;
  } catch (err) {
    console.error('解析代码失败:', (err as Error).message);
    return [];
  }
};

/**
 * 从注释中提取特定标签的值
 * @param comments - 注释节点数组
 * @param tag - 标签名（如 'description' 或 'summary'）
 * @returns 标签对应的值
 */
const extractCommentValue = (comments: Array<CommentBlock | CommentLine>, tag: string): string | undefined => {
  for (const comment of comments) {
    if (comment.type === 'CommentBlock') {
      const lines = comment.value.split('\n');
      for (const line of lines) {
        const match = line.match(new RegExp(`@${tag}\\s+(.*)`));
        if (match) {
          return match[1].trim();
        }
      }
    }
  }
  return undefined;
};

/**
 * 从文件中提取导出的函数名
 * @param filePath - 文件路径
 * @returns 导出的函数名数组
 */
export const extractFunctionsFromFile = (filePath: string): FunctionInfo[] => {
  try {
    const code = fs.readFileSync(filePath, 'utf-8');
    return extractFunctionsFromCode(code);
  } catch (err) {
    console.error(`读取文件失败: ${path.basename(filePath)}`, (err as Error).message);
    return [];
  }
};

/**
 * 比较两个函数数组，输出新增和删除的函数
 * @param oldFunctions - 旧的函数数组
 * @param newFunctions - 新的函数数组
 * @returns 包含新增和删除函数信息的对象
 */
export const compareFunctions = (
  oldFunctions: Array<FunctionInfo>,
  newFunctions: Array<FunctionInfo>
): { added: typeof newFunctions; removed: typeof oldFunctions } => {
  // 提取函数名数组
  const oldFunctionNames = oldFunctions.map((func) => func.name);
  const newFunctionNames = newFunctions.map((func) => func.name);

  // 找出新增的函数
  const added = newFunctions.filter((func) => !oldFunctionNames.includes(func.name));

  // 找出删除的函数
  const removed = oldFunctions.filter((func) => !newFunctionNames.includes(func.name));

  return { added, removed };
};
/**
 * 生成多列 Markdown 表格
 * @param data - 表格数据（二维数组）
 * @param headers - 表头数组
 * @param title - 表格标题（可选）
 * @returns Markdown 表格字符串
 */
export const generateMultiColumnMarkdownTable = (
  data: Array<Array<string | undefined>>,
  headers: string[],
  title?: string
): string => {
  if (data.length === 0) {
    return title ? `**${title}**\n\n无数据` : '无数据';
  }

  // 生成表头
  let table = `| ${headers.join(' | ')} |\n`;
  table += `| ${headers.map(() => '---').join(' | ')} |\n`;

  // 生成表格内容
  data.forEach((row) => {
    table += `| ${row.map((cell) => cell || '').join(' | ')} |\n`;
  });

  // 添加标题（如果提供）
  if (title) {
    table = `**${title}**\n\n${table}`;
  }

  return table;
};
